<template>
  <v-content class="client analytics page">
    <v-progress-linear
      v-if="loading"
      :color="themes.client.light.input"
      indeterminate
      class="my-0"
    />

    <v-container>
      <v-layout column>
        <v-toolbar flat color="transparent">
          <v-toolbar-title>Analytics</v-toolbar-title>
          <v-spacer />

          <v-tooltip bottom>
            <span>View campaign analytics</span>
            <v-btn flat slot="activator"
                   to="/client/analytics/campaigns">
              Campaigns
            </v-btn>
          </v-tooltip>
          <v-tooltip bottom>
            <span>View segmented analytics</span>
            <v-btn outline round slot="activator"
                   to="/client/analytics/explorer"
                   :color="themes.client.light.input">
              Customize
              <v-icon right>mdi-equalizer</v-icon>
            </v-btn>
          </v-tooltip>
        </v-toolbar>
      </v-layout>
    </v-container>

    <!-- General Section -->
    <v-container grid-list-md>
      <v-layout row wrap>
        <v-flex xs12 sm6>
          <AnalyticsCard
            icon="mdi-desk-lamp"
            title="Recent Page Views"
            path="client/organization/analytics/page_views"
            info="Organization and opportunities pages in the past month"
          />
        </v-flex>

        <v-flex xs12 sm6>
          <AnalyticsCard
            icon="mdi-badge-account-outline"
            title="Recent Campaign Signups"
            path="client/organization/analytics/signups"
            info="Signups from your campaigns in the past month"
          />
        </v-flex>

        <v-flex xs12 sm6>
          <AnalyticsCard
            icon="mdi-ballot-outline"
            title="Live Applications"
            path="client/organization/analytics/active_applications"
            info="All unresolved applications"
          />
        </v-flex>

        <v-flex xs12 sm6>
          <AnalyticsCard
            icon="mdi-calendar-clock"
            title="Recent Applications"
            path="client/organization/analytics/recent_applications"
            info="New applications in the past month"
          />
        </v-flex>

        <v-flex xs12>
          <AnalyticsCard
            icon="mdi-chart-timeline"
            title="Avg Candidate Waiting Time"
            path="client/organization/analytics/time_to_action"
            info="Average time live applications have been in their current state"
          />
        </v-flex>
      </v-layout>
    </v-container>

    <!-- Opportunity Section -->
    <v-container grid-list-md>
      <v-layout row wrap>
        <v-flex xs12>
          <v-card flat color="transparent">
            <v-card-title class="px-0">
              Showing {{ filtered ? 'active' : 'all' }} opportunities
              <v-spacer />
              <v-btn flat @click="filtered = !filtered">
                Show {{ filtered ? 'all' : 'active' }}
                <v-icon right>{{ filtered ? 'mdi-toggle-switch-off-outline' : 'mdi-toggle-switch-outline' }}</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text v-if="selectedOpportunities.length === 0" class="text-xs-center">
              <em>No opportunities to show. Select Show All above or list a new opportunity to see more.</em>
            </v-card-text>
          </v-card>
        </v-flex>
        <OpportunityChart
          v-for="opportunity in selectedOpportunities"
          :key="`analytics-opportunity-${opportunity.id}`"
          :opportunity="opportunity"
        />
      </v-layout>
    </v-container>
  </v-content>
</template>

<script>
// Mixins
import { managedDocumentAttributes } from '@/mixins/managedDocumentAttributes'

// Components
import AnalyticsCard from '@/components/client/analytics/AnalyticsCard'
import OpportunityChart from '@/components/client/analytics/OpportunityChart'

// Helpers
import { mapGetters } from 'vuex'

// Vue Component
export default {
  mixins: [managedDocumentAttributes],
  components: { AnalyticsCard, OpportunityChart },
  created() {
    this.documentAttributes.title = `Analytics – Leaply`
    Promise.all([
      this.$store.dispatch('client/organization/fetch', this.$route.params.id).then(() => {
        this.documentAttributes.title = `${this.organization.name} Analytics - Leaply`
      }),
      this.$store.dispatch('client/opportunities/fetchAll'),
      this.$store.dispatch('client/campaigns/fetchAll'),
    ]).finally(() => (this.loading = false))
  },
  data() {
    return {
      loading: true,
      filtered: true,
    }
  },
  computed: {
    selectedOpportunities() {
      const filterFn = function(opp) {
        return new Date(opp.commencesAt) > new Date()
      }
      return this.opportunities.filter(this.filtered ? filterFn : (x => true) ).sort((one, other) => {
        if (one.commencesAt < other.commencesAt) {
          return 1
        } else if (one.commencesAt > other.commencesAt) {
          return -1
        } else {
          return 0
        }
      })
    },
    ...mapGetters({
      opportunities: 'client/opportunities/all',
      organization: 'client/organization/model',
    })
  },
}
</script>

<style lang="scss">
.client.analytics.page {
  .v-toolbar__content {
    padding-left: 0;
    padding-right: 0;
  }
}
</style>
